import { ThemeOptions, createTheme } from '@mui/material/styles';
import { css } from '@emotion/react';
import { getAssetsPrefix } from '../utils/buildVersion';
import { objectKeys } from '../utils/object-keys';
import { colors } from '../theme/colors';

const assetPrefix = getAssetsPrefix();
interface IDeviceSize {
    mobile: number;
    tablet: number;
    smallDesktop: number;
    desktop: number;
    midDesktop: number;
    bigDesktop: number;
}

export const sizes: IDeviceSize = {
    mobile: 0,
    tablet: 768,
    smallDesktop: 992,
    desktop: 1280,
    midDesktop: 1440,
    bigDesktop: 1920,
};

export const mediaQueries = {
    phone: `(min-width: ${sizes.mobile}px) and (max-width: ${sizes.tablet - 1}px)`,
    phoneAndUp: `(min-width: ${sizes.mobile}px)`, //this should be default anyways
    tablet: `(min-width: ${sizes.tablet}px) and (max-width: ${sizes.smallDesktop - 1}px) `,
    allTablet: `(min-width: ${sizes.tablet}px) and (max-width: ${sizes.desktop - 1}px) `,
    tabletAndUp: `(min-width: ${sizes.tablet}px)`,
    smallDesktop: `(min-width: ${sizes.smallDesktop}px) and (max-width: ${sizes.desktop - 1}px) `,
    smallDesktopAndUp: `(min-width: ${sizes.smallDesktop}px)`,
    desktop: `(min-width: ${sizes.desktop}px) and (max-width: ${sizes.midDesktop - 1}px) `,
    desktopAndUp: `(min-width: ${sizes.desktop}px)`,
    midDesktop: `(min-width: ${sizes.midDesktop}px) and (max-width: ${sizes.bigDesktop - 1}px) `,
    bigDesktop: `(min-width: ${sizes.bigDesktop}px)`,
    allDesktop: `(min-width: ${sizes.smallDesktop}px)`,
    allMobileDevices: `(min-width: ${sizes.mobile}px) and (max-width: ${sizes.smallDesktop - 1}px)`,

    IE10or11: 'all and (-ms-high-contrast: none), (-ms-high-contrast: active)',
};

export const media = objectKeys(sizes).reduce((acc: any, label) => {
    acc[label] = (args: TemplateStringsArray) => {
        return css`
            @media (min-width: ${sizes[label]}px) {
                ${css(`${args}`)}
            }
        `;
    };

    return acc;
}, {});

export type FontSize =
    | 'regular'
    | 'regularS'
    | 'headlineXXL'
    | 'headlineXL'
    | 'headlineL'
    | 'headlineM'
    | 'headlineS'
    | 'headlineXS'
    | 'copyTextXL'
    | 'copyTextL'
    | 'copyTextM'
    | 'copyTextS'
    | 'copyTextXS'
    | 'copyTextXSS'
    | 'copyTextXSSS';

type FontSizes = { [key in FontSize]: string | number };

const fontSize: FontSizes = {
    regular: '16px',
    regularS: '14px',
    headlineXXL: '38px',
    headlineXL: '32px',
    headlineL: '28px',
    headlineM: '24px',
    headlineS: '20px',
    headlineXS: '18px',
    copyTextXL: '16px',
    copyTextL: '15px',
    copyTextM: '13px',
    copyTextS: '12px',
    copyTextXS: '11px',
    copyTextXSS: '9px',
    copyTextXSSS: '7px',
};

type LineHeights = { [key in FontSize]?: string | number };

const lineHeight: LineHeights = {
    headlineXXL: '34px',
    headlineL: '23px',
    headlineM: '19px',
    headlineS: '20px',
    headlineXL: '31px',
    headlineXS: '18px',
};

export const colorsUnifications = {
    gunmetal: {
        100: '#1A181E',
        70: 'rgba(46, 52, 56, 0.7)',
        50: 'rgba(46, 52, 56, 0.5)',
    },
    white: {
        100: '#fff',
    },
    whiteSmoke: {
        100: '#F5F5F5',
    },
    soapstone: {
        100: '#FDFDFD',
    },
    desertStorm: {
        100: '#F8F8F8',
    },
    seashell: {
        100: '#F0F0F0',
    },
    aliceBlue: {
        100: '#F5FBFF',
    },
    cornFlower: {
        100: '#C4E7FF',
    },
    iron: {
        100: '#E0E1E3',
    },
    nebula: {
        100: '#C7D4DD',
    },
    azure: {
        100: '#0099FF',
        20: '#CCEBFF',
        4: 'rgba(0, 153, 255, 0.04)',
    },
    cerulean: {
        100: '#008AE7',
    },
    turtleGreen: {
        100: '#84BA4D',
        20: 'rgba(132, 186, 77, 0.2)',
        4: 'rgba(132, 186, 77, 0.04)',
    },
    indigo: {
        100: '#8F00FF',
        20: 'rgba(143, 0, 255, 0.2)',
        4: 'rgba(143, 0, 255, 0.04)',
    },
    golden: {
        100: '#FFB500',
        20: 'rgba(255, 181, 0, 0.2)',
        4: 'rgba(255, 181, 0, 0.04)',
    },
    heatWave: {
        100: '#FF7A00',
        20: 'rgba(255, 122, 0, 0.2)',
        4: 'rgba(255, 122, 0, 0.04)',
    },
    watermelon: {
        100: '#EE3D58',
        20: 'rgba(238, 61, 88, 0.20)',
        4: 'rgba(238, 61, 88, 0.04)',
    },
};

/** @TODO we should use MaterialUI interface to generate theme */
export const MuiTheme: ThemeOptions = {
    palette: {
        error: {
            main: colors.watermelon[100],
        },
    },
    typography: {
        fontFamily: 'NotoSans, Helvetica, sans-serif',
    },
    components: {
        MuiLink: {
            defaultProps: {
                color: colors.azure[100],
            },
        },
        MuiTypography: {
            defaultProps: {
                letterSpacing: 0,
            },
        },
    },
};

export const MUITheme = createTheme(MuiTheme);

export const theme = {
    media: {
        xsAndUp: `@media (min-width: ${MUITheme.breakpoints.values.xs}px)`,
        smAndUp: `@media (min-width: ${MUITheme.breakpoints.values.sm}px)`,
        mdAndUp: `@media (min-width: ${MUITheme.breakpoints.values.md}px)`,
        lgAndUp: `@media (min-width: ${MUITheme.breakpoints.values.lg}px)`,
    },
    // here we are trying to build new color system
    // https://www.figma.com/file/5ANdlxhBwhkqSgDzMDzLJo/Redesign-2.0-Library?node-id=2%3A693&t=wTxW6cMWhOy4RaTw-0
    colorsUnifications: colors,
    colors: {
        main1: '#00233b',
        'main1-80': 'rgba(0, 35, 59, 0.8)',
        'main1-70': 'rgba(0, 35, 59, 0.7)',
        'main1-50': 'rgba(0, 35, 59, 0.5)',
        'main1-30': 'rgba(0, 35, 59, 0.3)',
        'main1-20': 'rgba(0, 35, 59, 0.2)',
        main2: '#00355d',
        'main2-5': 'rgba(0, 53, 93, 0.05)',
        'main2-10': 'rgba(0, 53, 93, 0.10)',
        'main2-15': 'rgba(0, 53, 93, 0.15)',
        'main2-20': '#ccd7df',
        'main2-30': 'rgba(0, 53, 93, 0.3)',
        'main2-50': 'rgba(0, 53, 93, 0.5)',
        'main2-75': 'rgba(0, 53, 93, 0.75)',
        'main2-70': '#4e738f',
        main3: '#164e78',
        'main3-20': 'rgba(22, 78, 120, 0.2)',
        main4: '#006295',
        'main4-80': 'rgba(0, 98, 149, 0.8)',
        'main4-30': 'rgba(0, 98, 149, 0.3)',
        'main4-20': 'rgba(0, 98, 149, 0.2)',
        'main4-10': 'rgba(0, 98, 149, 0.1)',
        main5: '#0099ff',
        main6: '#9bc3d7',
        main7: '#008deb',
        main8: '#005682',
        main9: '#f3faff',
        main10: '#06aaee',
        discountSpecial: '#e01a59',
        'discountSpecial-20': '#f9d1de',
        success: '#84ba4d',
        topOffer: '#f4b00b',
        uspLabel: '#165383',
        'hints-15': 'rgba(244, 176, 11, 0.15)',
        dreamdealMain: '#9e1f63',
        dreamdealAdditional: '#ee3d58',
        deal1: '#31a8bc',
        deal2: '#1876cc',
        error: '#EE3D58',
        separator: '#979797',
        black: '#000000',
        highlightBorder: '#006598',
        deckInactive: '#e8eff4',
        macScrollBar: '#babac0',
    },
    labels: {
        shipRatings: '#459bb6',
        onlineBookable: '#07496f',
        quote: '#2e9c57',
        bestseller: '#ff9900',
        urgency: '#8d022e',
        routeRatings: '#509f00',
        highDemand: '#de4a3b',
        cabinRatings: '#0d81a6',
        gastroRating: '#098398',
        entertainmentRating: '#45a6b6',
        serviceRating: '#598ea0',
        greyedLabel: '#979797',
    },
    shades: {
        blackGrey: '#333333',
        darkGrey: '#4a4a4a',
        darkMidGrey: '#7d7d7d',
        midGrey: '#9b9b9b',
        lightMidGrey: '#c3c3c3',
        lightGrey: '#d8d8d8',
        darkWhiteGrey: '#f7f7f7',
        whiteGrey: '#f0f0f0',
        grey6: '#2a2a2a',
        white: '#ffffff',
        'white-90': 'rgba(255, 255, 255, 0.9)',
        'white-70': 'rgba(255, 255, 255, 0.7)',
        textFadeGradient: 'rgba(22, 83, 131, 0.7)',
    },
    dropShadows: {
        'black-8': 'rgba(0, 0, 0, 0.08)',
        'black-20': 'rgba(0, 0, 0, 0.2)',
        'black-30': 'rgba(0, 0, 0, 0.3)',
        'black-50': 'rgba(0, 0, 0, 0.5)',
        'black-80': 'rgba(0, 0, 0, 0.8)',
        'black-70': 'rgba(0, 0, 0, 0.7)',
        'black-90': 'rgba(0, 0, 0, 0.9)',
    },
    boxShadow: {
        modalInput: '0 8px 7px 0 rgba(0, 0, 0, 0.11)',
    },
    spacingX: {
        mobile: '10px',
        tablet: '15px',
        desktop: '20px',
    },
    spacing: {
        small: '5px',
        medium: '10px',
        large: '15px',
        extraLarge: '20px',
        xxLarge: '25px',
        xxxLarge: '30px',
        xxxxLarge: '40px',
        landingComponent: {
            small: '30px',
            medium: '50px',
            large: '60px',
        },
    },
    padding: {
        default: '15px',
    },
    fontSize,
    lineHeight,
    transition: {
        duration: {
            modal: 400,
        },
    },
};

// @TODO: move this to a css file and load it on document as import *.css
// instead of inserting it with dangerouslySetElement
// ! Do not put this in a css template string, this is cursed (also can't handle assetsPrefix)
export const fontStyle = `
    @font-face {
        font-family: 'NotoSans';
        font-display: swap;
        src: url('${assetPrefix}/static/webfonts/notosans-bold-webfont.eot');
        src: url('${assetPrefix}/static/webfonts/notosans-bold-webfont.eot?#iefix') format('embedded-opentype'),
            url('${assetPrefix}/static/webfonts/notosans-bold-webfont.woff2') format('woff2'),
            url('${assetPrefix}/static/webfonts/notosans-bold-webfont.woff') format('woff'),
            url('${assetPrefix}/static/webfonts/notosans-bold-webfont.ttf') format('truetype'),
            url('${assetPrefix}/static/webfonts/notosans-bold-webfont.svg#noto_sansbold') format('svg');
        font-weight: 700;
        font-style: normal;
    }
    @font-face {
        font-family: 'NotoSans';
        font-display: swap;
        src: url('${assetPrefix}/static/webfonts/notosans-semibold-webfont.eot');
        src: url('${assetPrefix}/static/webfonts/notosans-semibold-webfont.eot?#iefix') format('embedded-opentype'),
            url('${assetPrefix}/static/webfonts/notosans-semibold-webfont.woff2') format('woff2'),
            url('${assetPrefix}/static/webfonts/notosans-semibold-webfont.woff') format('woff'),
            url('${assetPrefix}/static/webfonts/notosans-semibold-webfont.ttf') format('truetype'),
            url('${assetPrefix}/static/webfonts/notosans-semibold-webfont.svg#noto_sanssemibold') format('svg');
        font-weight: 600;
        font-style: normal;
    }
    @font-face {
        font-family: 'NotoSans';
        font-display: swap;
        src: url('${assetPrefix}/static/webfonts/notosans-medium-webfont.eot');
        src: url('${assetPrefix}/static/webfonts/notosans-medium-webfont.eot?#iefix') format('embedded-opentype'),
            url('${assetPrefix}/static/webfonts/notosans-medium-webfont.woff2') format('woff2'),
            url('${assetPrefix}/static/webfonts/notosans-medium-webfont.woff') format('woff'),
            url('${assetPrefix}/static/webfonts/notosans-medium-webfont.ttf') format('truetype'),
            url('${assetPrefix}/static/webfonts/notosans-medium-webfont.svg#noto_sansmedium') format('svg');
        font-weight: 500;
        font-style: normal;
    }
    @font-face {
        font-family: 'NotoSans';
        font-display: swap;
        src: url('${assetPrefix}/static/webfonts/notosans-regular-webfont.eot');
        src: url('${assetPrefix}/static/webfonts/notosans-regular-webfont.eot?#iefix') format('embedded-opentype'),
            url('${assetPrefix}/static/webfonts/notosans-regular-webfont.woff2') format('woff2'),
            url('${assetPrefix}/static/webfonts/notosans-regular-webfont.woff') format('woff'),
            url('${assetPrefix}/static/webfonts/notosans-regular-webfont.ttf') format('truetype'),
            url('${assetPrefix}/static/webfonts/notosans-regular-webfont.svg#noto_sansregular') format('svg');
        font-weight: 400;
        font-style: normal;
    }
    body {
        font-family: NotoSans, Helvetica, sans-serif;
    }
`;

// @TODO: move this to a css file and load it on document as import *.css
// instead of inserting it with dangerouslySetElement
export const headerStyle = css`
    h1,
    h2,
    h3,
    h4 {
        font-style: normal;
        font-weight: 700;
    }
    h1 {
        font-size: 16px;
        line-height: 22px;
    }
    h2 {
        font-size: 18px;
        line-height: 24px;
    }
    h3 {
        font-size: 34px;
        line-height: 45px;
    }
    h4 {
        font-size: 21px;
        line-height: 26px;
    }
    p {
        font-size: 14px;
        line-height: 21px;
    }
    ul {
        margin: 0;
    }
    @media ${mediaQueries.tablet} {
        h1 {
            font-size: 28px;
            line-height: 32px;
        }
        h2 {
            font-size: 20px;
            line-height: 28px;
        }
        h3 {
            font-size: 32px;
            line-height: 40px;
        }
        h4 {
            font-size: 19px;
        }
        p {
            font-size: 12px;
        }
    }
    @media ${mediaQueries.smallDesktop} {
        h2 {
            font-size: 25px;
            line-height: 35px;
        }
        h3 {
            font-size: 38px;
            line-height: 50px;
        }
        h4 {
            font-size: 18px;
        }
    }
`;

// @TODO: move this to a css file and load it on document as import *.css
// instead of inserting it with dangerouslySetElement
export const GlobalStyle = `
    ${fontStyle}
    *, :after, :before {
        box-sizing: border-box;
    }
    /*
     * scroll-behavior for smooth scroll in supported browsers. We can use window.scrollTo for IE11
     * https://caniuse.com/#search=scroll-behavior
     */
    html {
        scroll-behavior: smooth;
    }
    body {
        font-family: NotoSans, Helvetica, sans-serif;
        background-color: ${theme.shades.whiteGrey};
        /* @TODO: color should be the blackGrey Color. White should be only be set explictly if needed per component */
        color: ${theme.shades.white};
        box-sizing: border-box;
    }
    html, body {
        font-size: 12px;
    }

    ${headerStyle.styles}

    .DayPicker-wrapper {
        outline: none;
    }
`;
/*Extend this style for hiding element visually but preserve the accessibility.*/
export const visuallyHidden = () => {
    return css`
        border: 0;
        clip: rect(1px 1px 1px 1px);
        clip: rect(1px, 1px, 1px, 1px);
        height: 1px;
        margin: -1px;
        overflow: hidden;
        padding: 0;
        position: absolute;
        width: 1px;
    `;
};

export const customWebkitScrollbar = () => css`
    ::-webkit-scrollbar {
        width: 11px;
    }

    ::-webkit-scrollbar-thumb {
        background-color: ${theme.shades.lightGrey};
        border-radius: 5px;
        border: 3px solid transparent;
        background-clip: padding-box;
    }
`;
